import type {
  VoucherEligibilityItem,
  VoucherGetByCodeResponse,
  VoucherWithAllowOther,
} from '@diamond/shared/types';
import { useCallback, useMemo } from 'react';

/**
 * Input parameters for useVoucherCombinationRules.
 *
 * This hook is intentionally UI-agnostic and only encapsulates
 * voucher combination rules based on `allow_other_vouchers`.
 */
export interface UseVoucherCombinationRulesParams {
  eligibilityVouchers?: VoucherEligibilityItem[];
  searchedVoucher?: VoucherGetByCodeResponse | null;
  selectedVoucherForDetails?: VoucherWithAllowOther | null;
  appliedVoucherCodes: string[];
  isVoucherApplied: (code: string) => boolean;
}

/**
 * Result of useVoucherCombinationRules.
 */
export interface UseVoucherCombinationRulesResult {
  isVoucherDisabledByCombination: (code: string) => boolean;
  getCombinationDisabledReason: (code: string) => string | null;
}

/**
 * useVoucherCombinationRules
 *
 * Encapsulates business rules for combining vouchers based on allow_other_vouchers:
 *
 * - Non-combinable (allow_other_vouchers === false):
 *   - If any such voucher is applied, it must be the only voucher in use.
 *   - When one is applied, all other vouchers are disabled.
 *
 * - Combinable (allow_other_vouchers === true):
 *   - May be combined with other combinable vouchers.
 *   - When any combinable voucher is applied:
 *     - All non-combinable vouchers become disabled.
 *
 * This hook focuses purely on combination logic. It does NOT:
 * - Check eligibility (is_eligible) flags.
 * - Perform side-effects or UI updates.
 * Eligibility and UI concerns should be layered on top by consumers.
 */
export const useVoucherCombinationRules = (
  params: UseVoucherCombinationRulesParams
): UseVoucherCombinationRulesResult => {
  const {
    eligibilityVouchers,
    searchedVoucher,
    selectedVoucherForDetails,
    appliedVoucherCodes,
    isVoucherApplied,
  } = params;

  /**
   * Build a normalized map: code -> allow_other_vouchers
   * from all known voucher sources.
   */
  const allowOtherByCode = useMemo(() => {
    const mapAllowOther = (
      vouchers: VoucherWithAllowOther[] | undefined
    ): Record<string, boolean> => {
      if (!vouchers || vouchers.length === 0) {
        return {};
      }

      return vouchers.reduce<Record<string, boolean>>((acc, voucher) => {
        const code = voucher.code?.trim();
        if (!code) {
          return acc;
        }

        if ('allow_other_vouchers' in voucher) {
          acc[code] = Boolean(voucher.allow_other_vouchers);
        }

        return acc;
      }, {});
    };

    const eligibility =
      eligibilityVouchers && eligibilityVouchers.length > 0
        ? mapAllowOther(eligibilityVouchers)
        : {};

    const fromSearch = searchedVoucher ? mapAllowOther([searchedVoucher]) : {};

    const fromDetails = selectedVoucherForDetails
      ? mapAllowOther([selectedVoucherForDetails])
      : {};

    return {
      ...eligibility,
      ...fromSearch,
      ...fromDetails,
    };
  }, [eligibilityVouchers, searchedVoucher, selectedVoucherForDetails]);

  const getAllowOtherForCode = useCallback(
    (code: string): boolean | undefined => {
      const normalized = code.trim();
      if (!normalized) {
        return undefined;
      }

      if (normalized in allowOtherByCode) {
        return allowOtherByCode[normalized];
      }

      return undefined;
    },
    [allowOtherByCode]
  );

  /**
   * Compute current combination state based on applied vouchers.
   */
  const combinationState = useMemo(() => {
    const nonCombinableAppliedCodes: string[] = [];
    const combinableAppliedCodes: string[] = [];

    appliedVoucherCodes.forEach((rawCode) => {
      const code = rawCode.trim();
      if (!code) {
        return;
      }

      const allowOther = getAllowOtherForCode(code);

      if (allowOther === false) {
        nonCombinableAppliedCodes.push(code);
      } else if (allowOther === true) {
        combinableAppliedCodes.push(code);
      }
    });

    return {
      hasNonCombinableApplied: nonCombinableAppliedCodes.length > 0,
      hasCombinableApplied: combinableAppliedCodes.length > 0,
      nonCombinableAppliedCodes,
      combinableAppliedCodes,
    };
  }, [appliedVoucherCodes, getAllowOtherForCode]);

  const {
    hasNonCombinableApplied,
    hasCombinableApplied,
    nonCombinableAppliedCodes,
  } = combinationState;

  /**
   * Determine if a voucher (by code) is disabled by combination rules.
   *
   * NOTE:
   * - This is combination-only. Callers should also consider:
   *   - eligibility flags (is_eligible)
   *   - stock, quota, or other backend constraints
   */
  const isVoucherDisabledByCombination = (rawCode: string): boolean => {
    const code = rawCode.trim();
    if (!code) {
      return false;
    }

    const isApplied = isVoucherApplied(code);
    const allowOther = getAllowOtherForCode(code);

    // Case 1: A non-combinable voucher is applied.
    // Only that/those non-combinable codes remain enabled; all others disabled.
    if (hasNonCombinableApplied) {
      const isOneOfNonCombinable = nonCombinableAppliedCodes.includes(code);

      if (isOneOfNonCombinable) {
        // The applied non-combinable itself must remain enabled (for unapply).
        return false;
      }

      return true;
    }

    // Case 2: At least one combinable voucher is applied.
    // Any non-combinable voucher that is not already applied is disabled.
    if (hasCombinableApplied) {
      if (allowOther === false && !isApplied) {
        return true;
      }

      return false;
    }

    // Case 3: No vouchers applied or unknown configuration.
    // No combination-based restriction.
    return false;
  };

  /**
   * Human-readable reason when a voucher is disabled by combination rules.
   */
  const getCombinationDisabledReason = (code: string): string | null => {
    if (isVoucherDisabledByCombination(code)) {
      return 'Voucher ini tidak dapat digunakan bersamaan dengan voucher lain yang sedang digunakan.';
    }

    return null;
  };

  return {
    isVoucherDisabledByCombination,
    getCombinationDisabledReason,
  };
};
